// ─── Command Dispatcher ───────────────────────────────────────────────────────
//
// ET-7 enforcement: a Command Event may have zero or exactly one registered
// consumer at any time. Registering a second consumer throws
// CommandConsumerConflictError at registration time.
//
// Command Events express intent to an external subsystem (routing runtime,
// UI renderer). They are distinct from Outcome Events, which describe completed
// facts. This distinction is governed by the Layer 2C Event Taxonomy.
//
// Replay suppression is the emitter's responsibility (actionExecutor checks
// replayEngine.isReplayMode() before calling commandDispatcher.emit). This file
// has no knowledge of replay mode.
//
// Import path for all layers: @/events/commandDispatcher
// Do NOT import directly from @/runtime/eventBus to emit Command Events.

import { eventBus } from '@/runtime/eventBus';
import type { NuvraEvent } from '@/runtime/eventBus';

// ─── Command Event Types ──────────────────────────────────────────────────────

// The closed set of Command Event types registered in the NuvraEvent union.
// Grows only when a new Command Event is added to NuvraEvent and explicitly
// classified as a Command Event through the Event Taxonomy process.
export type CommandEventType = 'app:navigate' | 'app:notification';

// ─── Error ────────────────────────────────────────────────────────────────────

export class CommandConsumerConflictError extends Error {
  constructor(
    public readonly eventType: string,
    public readonly existingConsumer: string,
  ) {
    super(
      `Command event "${eventType}" already has a registered consumer ("${existingConsumer}"). ` +
      `Only one consumer is permitted per Command Event. Call the unsubscribe function ` +
      `returned by the first registerCommandConsumer() call before registering a new consumer.`
    );
    this.name = 'CommandConsumerConflictError';
  }
}

// ─── Types ────────────────────────────────────────────────────────────────────

type CommandEvent = Extract<NuvraEvent, { type: CommandEventType }>;

type CommandListener<T extends CommandEventType> = (
  event: Extract<NuvraEvent, { type: T }>
) => void;

// ─── Dispatcher ───────────────────────────────────────────────────────────────

class CommandDispatcherImpl {
  private consumers = new Map<string, { label: string; eventBusUnsub: () => void }>();

  /**
   * Register exactly one consumer for a Command Event type.
   *
   * Throws CommandConsumerConflictError if a consumer is already registered.
   * Returns an unsubscribe function - calling it removes the consumer and
   * allows a new consumer to register.
   *
   * @param type         The Command Event type to consume.
   * @param consumer     The handler function.
   * @param consumerLabel  Human-readable label used in conflict error messages.
   */
  registerCommandConsumer<T extends CommandEventType>(
    type: T,
    consumer: CommandListener<T>,
    consumerLabel: string,
  ): () => void {
    const existing = this.consumers.get(type);
    if (existing) {
      throw new CommandConsumerConflictError(type, existing.label);
    }

    // Subscribe to the underlying eventBus.
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const eventBusUnsub = eventBus.on(type, consumer as any);

    const deregister = () => {
      eventBusUnsub();
      this.consumers.delete(type);
    };

    this.consumers.set(type, { label: consumerLabel, eventBusUnsub });
    return deregister;
  }

  /**
   * Emit a Command Event. Delegates to eventBus.emit.
   *
   * The caller (actionExecutor) is responsible for suppressing this during
   * replay by checking replayEngine.isReplayMode() before calling.
   */
  emit<T extends CommandEventType>(event: Extract<NuvraEvent, { type: T }>): void {
    eventBus.emit(event as CommandEvent);
  }

  /** Whether a consumer is currently registered for the given Command Event type. */
  hasConsumer(type: CommandEventType): boolean {
    return this.consumers.has(type);
  }

  /**
   * Reset all registered consumers. Intended for test isolation only.
   * Removes all consumers from the eventBus and clears the internal registry.
   */
  reset(): void {
    for (const { eventBusUnsub } of this.consumers.values()) {
      eventBusUnsub();
    }
    this.consumers.clear();
  }
}

export const commandDispatcher = new CommandDispatcherImpl();
